import { useMutation, useQueryClient } from "@tanstack/react-query";
import { confirmCancelBooking } from "api/booking.api";
import { useToast } from "hooks";
import { useState } from "react";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { Modal } from "ui/base/modal/Modal";
import { TextArea } from "ui/base/textArea/TextArea";
import { themeFontSize } from "ui/theme/index.js";

export const DeclineModal = ({
  show,
  handleClose,
  selectedBooking,
  cancellationData,
}) => {
  const queryClient = useQueryClient();

  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const { mutate, isLoading } = useMutation({
    mutationFn: confirmCancelBooking,
    onError: (error) => {
      useToast(
        "error",
        error?.response?.data?.message || "Error: cancelBookingTeacher"
      );
    },
    onSuccess: () => {
      useToast("success", "Your class has been canceled");
      queryClient.invalidateQueries(["getFutureClassesForTeacher2"]);
      handleClose();
    },
  });

  const handleAccept = () => {
    if (!text.trim()) {
      setError("Instruction message cannot be empty.");
      return;
    } else if (text.length < 25) {
      setError("Must have over 25 characters.");
      return;
    }
    setError("");
    mutate({
      ...cancellationData,
      booking_id: selectedBooking?.booking_id || 0,
      reason: text,
      type: 2,
    });
  };

  return (
    <Modal show={show} handleClose={handleClose} minHeight={400} maxWidth={600}>
      <Section>
        <HeaderWrapper>
          <ClassInfo>
            <Info>
              <Name>Please indicate the reason for canceling the class</Name>
            </Info>
          </ClassInfo>
        </HeaderWrapper>
        <Container>
          <FormWrapper>
            <TextArea
              placeholder="Type your reason..."
              value={text}
              setValue={(text) => {
                setText(text);
                if (text.trim()) setError("");
              }}
              maxLength={1000}
              minLength={25}
              isError={!!error}
              errorText={error}
            />
          </FormWrapper>

          <ButtonPrimary disabled={isLoading} onClick={handleAccept}>
            Cancel Class
          </ButtonPrimary>
          <ButtonPrimary
            disabled={isLoading}
            onClick={handleClose}
            variant="outline"
          >
            Back to upcoming classes
          </ButtonPrimary>
        </Container>
      </Section>
    </Modal>
  );
};

const Section = styled.div`
  font-family: var(--font-secondary);
  flex-direction: column;
  padding: 1rem;
  display: flex;
  gap: 1rem;
`;

const HeaderWrapper = styled.div`
  flex-direction: column;
  display: flex;
  gap: 1rem;
`;

const ClassInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 1rem;
`;

const Info = styled.div`
  flex: 1;
  display: grid;
  gap: 1rem;
`;

const Name = styled.div`
  font-size: ${themeFontSize("regular")};
  font-weight: bold;
`;

const Container = styled.div`
  display: grid;
  gap: 1rem;
`;
const FormWrapper = styled.div`
  display: grid;
  gap: 1rem;

  & textarea {
    min-height: 200px;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: ${themeFontSize("small")};
  font-weight: bold;
`;
